import React, { useState, useEffect } from 'react';
import { categoryService } from '../services/categoryService';

function ExpenseFilters({ onFiltersChange, currentFilters }) {
  const [startDate, setStartDate] = useState(currentFilters.startDate || '');
  const [endDate, setEndDate] = useState(currentFilters.endDate || '');
  const [category, setCategory] = useState(currentFilters.category || '');
  const [importTemplate, setImportTemplate] = useState(currentFilters.importTemplate || '');
  const [categories, setCategories] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      // Load both flat and hierarchical categories
      const [hierarchicalData, flatData] = await Promise.all([
        categoryService.getCategories(),
        categoryService.getCategoriesFlat()
      ]);
      
      let hierarchicalCategories = hierarchicalData.categories || [];
      
      // Create ordered list for dropdowns (parent categories followed by their subcategories)
      const orderedCategories = [];
      
      // Sort parent categories alphabetically
      hierarchicalCategories.sort((a, b) => a.name.localeCompare(b.name));
      
      hierarchicalCategories.forEach(parentCategory => {
        // Add parent category
        orderedCategories.push(parentCategory);
        
        // Add subcategories if they exist, sorted alphabetically
        if (parentCategory.subcategories && parentCategory.subcategories.length > 0) {
          const sortedSubcategories = [...parentCategory.subcategories].sort((a, b) => a.name.localeCompare(b.name));
          orderedCategories.push(...sortedSubcategories);
        }
      });
      
      setCategories(orderedCategories);
      setHierarchicalCategories(hierarchicalCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  // Check if selected category is a parent with subcategories
  const getSelectedCategoryInfo = () => {
    if (!category) return null;
    
    const parentCategory = hierarchicalCategories.find(cat => 
      cat.name === category && 
      cat.subcategories && 
      cat.subcategories.length > 0
    );
    
    return parentCategory;
  };

  const handleApplyFilters = () => {
    const filters = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (category) filters.category = category;
    if (importTemplate) filters.importTemplate = importTemplate;
    
    onFiltersChange(filters);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCategory('');
    setImportTemplate('');
    onFiltersChange({});
  };

  const getQuickDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const hasActiveFilters = startDate || endDate || category || importTemplate;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            background: hasActiveFilters ? 'rgba(255, 107, 53, 0.2)' : 'rgba(255, 107, 53, 0.1)',
            border: hasActiveFilters ? '1px solid #ff6b35' : '1px solid #404040',
            color: hasActiveFilters ? '#ff6b35' : '#b0b0b0',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          🔍 Filters {hasActiveFilters && `(${Object.keys(currentFilters).length})`}
          <span style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
            ▼
          </span>
        </button>

        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            style={{
              background: 'rgba(255, 71, 87, 0.2)',
              border: '1px solid #ff4757',
              color: '#ff4757',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Clear All
          </button>
        )}
      </div>

      {showFilters && (
        <div style={{
          background: 'rgba(255, 107, 53, 0.05)',
          border: '1px solid #404040',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: window.innerWidth <= 768 ? '12px' : '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#f0f0f0', 
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#f0f0f0', 
                fontWeight: '600',
                fontSize: '14px'
              }}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#f0f0f0', 
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => {
                  // Check if this is a subcategory by looking for parentId or parent info
                  const isSubcategory = cat.parentId || cat.parent;
                  const displayName = isSubcategory 
                    ? `  ↳ ${cat.name}` // Indent subcategories with arrow
                    : cat.name;
                  
                  return (
                    <option key={cat.id} value={cat.name}>
                      {cat.icon} {displayName}
                    </option>
                  );
                })}
              </select>
              
              {/* Show info when parent category is selected */}
              {(() => {
                const selectedParent = getSelectedCategoryInfo();
                if (selectedParent) {
                  return (
                    <div style={{
                      marginTop: '6px',
                      padding: '8px',
                      background: 'rgba(52, 152, 219, 0.1)',
                      border: '1px solid rgba(52, 152, 219, 0.3)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#3498db'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>ℹ️</span>
                        <span>
                          Includes {selectedParent.name} and {selectedParent.subcategories.length} subcategory{selectedParent.subcategories.length !== 1 ? 'ies' : 'y'}:
                        </span>
                      </div>
                      <div style={{ marginTop: '4px', marginLeft: '16px' }}>
                        {selectedParent.subcategories.map((sub, index) => (
                          <span key={sub.id} style={{ marginRight: '8px' }}>
                            {sub.icon} {sub.name}{index < selectedParent.subcategories.length - 1 ? ',' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#f0f0f0', 
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Import Source
              </label>
              <select
                value={importTemplate}
                onChange={(e) => setImportTemplate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              >
                <option value="">All Sources</option>
                <option value="manual">📝 Manual Entry</option>
                <option value="Default Template">📥 Default Template</option>
                <option value="Partners Bank New England">🏦 Partners Bank New England</option>
                <option value="Saco Valley Credit Union">🏦 Saco Valley Credit Union</option>
                <option value="Chase Credit Card">💳 Chase Credit Card</option>
                <option value="Discover Credit Card">💳 Discover Credit Card</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: '#f0f0f0', 
              fontWeight: '600',
              fontSize: '14px'
            }}>
              Quick Date Ranges
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 480 ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '8px'
            }}>
              {[
                { label: window.innerWidth <= 480 ? '7d' : 'Last 7 days', days: 7 },
                { label: window.innerWidth <= 480 ? '30d' : 'Last 30 days', days: 30 },
                { label: window.innerWidth <= 480 ? '90d' : 'Last 90 days', days: 90 },
                { label: window.innerWidth <= 480 ? 'Month' : 'This month', days: new Date().getDate() - 1 },
              ].map(range => (
                <button
                  key={range.label}
                  type="button"
                  onClick={() => getQuickDateRange(range.days)}
                  style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid #404040',
                    color: '#b0b0b0',
                    padding: window.innerWidth <= 480 ? '8px 6px' : '6px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                    fontWeight: '500',
                    minHeight: '36px'
                  }}
                >
                  {range.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate(today);
                }}
                style={{
                  background: 'rgba(255, 107, 53, 0.1)',
                  border: '1px solid #404040',
                  color: '#b0b0b0',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                Today
              </button>
            </div>
          </div>

          <div style={{ 
            background: 'rgba(52, 152, 219, 0.1)',
            border: '1px solid rgba(52, 152, 219, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: '#3498db',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <span>ℹ️</span>
              <span>When filters are applied, all matching expenses will be loaded (not limited to 50)</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleApplyFilters}
              className="btn"
              style={{ flex: 1 }}
            >
              Apply Filters
            </button>
            <button
              onClick={handleClearFilters}
              style={{
                flex: 1,
                background: 'rgba(102, 102, 102, 0.2)',
                border: '1px solid #666',
                color: '#b0b0b0',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div style={{ 
          marginTop: '12px',
          fontSize: '14px',
          color: '#b0b0b0'
        }}>
          <strong>Active filters:</strong>
          {startDate && <span style={{ marginLeft: '8px', color: '#ff6b35' }}>From: {startDate}</span>}
          {endDate && <span style={{ marginLeft: '8px', color: '#ff6b35' }}>To: {endDate}</span>}
          {category && (
            <span style={{ marginLeft: '8px', color: '#ff6b35' }}>
              Category: {category}
              {(() => {
                const selectedParent = getSelectedCategoryInfo();
                if (selectedParent) {
                  return (
                    <span style={{ color: '#3498db', fontSize: '12px' }}>
                      {' '}(includes {selectedParent.subcategories.length} subcategory{selectedParent.subcategories.length !== 1 ? 'ies' : 'y'})
                    </span>
                  );
                }
                return null;
              })()}
            </span>
          )}
          {importTemplate && <span style={{ marginLeft: '8px', color: '#ff6b35' }}>Source: {importTemplate}</span>}
        </div>
      )}
    </div>
  );
}

export default ExpenseFilters;